// RecruitBoardAdd.tsx
import { observer } from "mobx-react-lite";
import MainLayout from "../MainLayout";
import { useLocation, useNavigate } from "react-router-dom";
import { VerticalLayout } from "../../common/common-layout";
import { TitleLargeMedium, TitleMediumBold } from "../../common/common-text";
import { useState, useEffect } from "react";
import BTEditor from "../../common/components/board/BTEditor";
import { authStore } from "../../store/AuthStore";
import { baseStore } from "../../store/BaseStore";
import BTBox from "../../common/components/BTBox";
import { BTSelectBox } from "../../common/components/BTSelectBox";
import { BTDateComponent } from "../../common/components/BTDateComponent";
import { BTCheckBox } from "../../common/components/BTCheckBox";
import {
  requestCreateRecruitDocuments,
  requestUpdateRecruitDocuments,
  requestUploadFiles
} from "../../service/BoardService";
import { dialogStore } from "../../store/DialogStore";
import Recruit from "../../model/board/Recruit";
import Divider from "@mui/material/Divider";
import theme from "../../common/theme";

// 오늘 날짜(YYYY-MM-DD)를 서울 시간대 기준으로 계산
const getTodayInSeoul = () => {
  const now = new Date();
  const seoulNow = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
  const y = seoulNow.getFullYear();
  const m = String(seoulNow.getMonth() + 1).padStart(2, '0');
  const d = String(seoulNow.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// 날짜 문자열을 UTC+9 오전 9시로 변환하는 함수
const createDateAt9AM = (dateString: string): Date => {
  // UTC+9 시간대에서 오전 9시로 설정
  const date = new Date(dateString + 'T09:00:00+09:00');
  return date;
};

const RecruitBoardAdd = () => (
  <MainLayout>
    <RecruitBoardAddBody />
  </MainLayout>
);

export default RecruitBoardAdd;

const RecruitBoardAddBody = observer(() => {
  const location = useLocation();
  const navigate = useNavigate();

  const [jobTitle, setJobTitle] = useState("");
  const [contents, setContents] = useState("");
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadedFileUrls, setUploadedFileUrls] = useState<string[]>([]);
  const [jobType, setJobType] = useState("");
  const [recruitStartDate, setRecruitStartDate] = useState(() => getTodayInSeoul());
  const [recruitEndDate, setRecruitEndDate] = useState("");
  const [isPublished, setIsPublished] = useState(true);

  const [recruitTypeOptions, setRecruitTypeOptions] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    const options = location.state;
    if (options) {
        setRecruitTypeOptions(options);
        setJobType(options[0].value);
    }

  }, [location.state]);

  const handleSave = async () => {
    try {
      baseStore.showLoading();

      const now = new Date();
      const recruit: Recruit = {
        jobType,
        jobTitle,
        contents: "",
        recruitStartDate: createDateAt9AM(recruitStartDate),
        recruitEndDate: recruitEndDate ? createDateAt9AM(recruitEndDate) : null,
        isPublished,
        attachedFiles: [],
        createdBy: authStore.user?.username ?? "",
        createdAt: now,
        updatedBy: authStore.user?.username ?? "",
        updatedAt: now,
      };

      // 1) 우선 문서 생성
      const docId = await requestCreateRecruitDocuments(recruit);
      recruit.id = docId;

      // 2) 에디터의 base64 이미지 업로드 & 치환
      const parser = new DOMParser();
      const docParsed = parser.parseFromString(contents, "text/html");
      const imgElements = docParsed.querySelectorAll("img");

      const base64ImageSrcs: string[] = [];
      const imagesToUpload: File[] = [];

      imgElements.forEach((img) => {
        const src = img.getAttribute("src");
        if (src && src.startsWith("data:")) base64ImageSrcs.push(src);
      });

      for (const base64Src of base64ImageSrcs) {
        const res = await fetch(base64Src);
        const blob = await res.blob();
        const fileName = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.png`;
        imagesToUpload.push(new File([blob], fileName, { type: blob.type }));
      }

      const uploadedImageUrls =
        imagesToUpload.length > 0
          ? await requestUploadFiles("recruit", docId, "image", imagesToUpload)
          : [];

      let idx = 0;
      imgElements.forEach((img) => {
        const src = img.getAttribute("src");
        if (src && src.startsWith("data:")) {
          const newUrl = uploadedImageUrls[idx++];
          if (newUrl) img.setAttribute("src", newUrl);
          if (selectedImage === src) setSelectedImage(newUrl);
        }
      });

      // 3) 첨부파일 업로드
      const uploadedAttachmentUrls =
        uploadedFiles.length > 0
          ? await requestUploadFiles("recruit", docId, "attachments", uploadedFiles)
          : [];
           
      const imgElements2 = docParsed.querySelectorAll("img");
      imgElements2.forEach((img) => {
        const imgWidth = img.getAttribute("width");
        const imgHeight = img.getAttribute("height");

        if (!imgWidth || !imgHeight) {
          const parentSpan = img.closest("span");
          if (parentSpan) {
            const spanWidth = parentSpan.getAttribute("width");
            const spanHeight = parentSpan.getAttribute("height");

            if (spanWidth) img.setAttribute("width", spanWidth);
            if (spanHeight) img.setAttribute("height", spanHeight);
          }
        }
      });
    
      // 최종적으로 contents에 반영
      recruit.attachedFiles = uploadedAttachmentUrls;
      recruit.contents = docParsed.body.innerHTML;

      await requestUpdateRecruitDocuments(recruit);

      dialogStore.showDialog({
        title: "성공",
        message: "채용 공고가 성공적으로 등록되었습니다.",
        onConfirm: () => navigate(-1),
        onClose: () => navigate(-1),
      });
    } catch (error) {
      dialogStore.showDialog({
        title: "오류",
        message: "채용 공고 등록 중 문제가 발생했습니다. 다시 시도해 주세요.",
        onConfirm: () => { },
      });
    } finally {
      baseStore.hideLoading();
    }
  };

  return (
    <VerticalLayout height="auto" paddingLeft={36} paddingRight={36}>
      <TitleLargeMedium>채용공고 작성</TitleLargeMedium>
      <BTBox height="24px" />
      <Divider style={{ width: "100%", backgroundColor: '#D9D9D9' }} />

      <VerticalLayout height={"auto"} paddingBottom={36}>
        {/* 섹션 타이틀 */}
        <TitleMediumBold style={{ marginTop: "20px" }}>채용공고 정보</TitleMediumBold>
        <BTBox height="16px" />

        {/* 1줄: 채용 유형 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexWrap: "nowrap",
            fontSize: "14px",
            color: "#6C757D",
          }}
        >
          <b style={{ whiteSpace: "nowrap" }}>채용 유형</b>
          {/* select가 100% 먹지 않도록 래퍼로 폭 제어 */}
          <div style={{ width: 260, flex: "0 0 auto" }}>
            <BTSelectBox
              width="100%"
              options={recruitTypeOptions}
              value={jobType}
              onChange={(e) => setJobType(e.target.value)}
            />
          </div>
        </div>

        <BTBox height="16px" />

        {/* 2줄: 시작일, 종료일, 공개 여부 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexWrap: "nowrap",
            fontSize: "14px",
            color: "#6C757D",
          }}
        >
          <b style={{ whiteSpace: "nowrap" }}>채용 시작 일자</b>
          <div style={{ width: 220, flex: "0 0 auto" }}>
            <BTDateComponent width="100%" value={recruitStartDate} onChange={setRecruitStartDate} />
          </div>

          <Divider orientation="vertical" sx={{ borderColor: "#6C757D", mx: "4px", height: "16px" }} />

          <b style={{ whiteSpace: "nowrap" }}>채용 종료 일자</b>
          <div style={{ width: 220, flex: "0 0 auto" }}>
            <BTDateComponent width="100%" value={recruitEndDate} onChange={setRecruitEndDate} />
          </div>

          <Divider orientation="vertical" sx={{ borderColor: "#6C757D", mx: "4px", height: "16px" }} />

          <b style={{ whiteSpace: "nowrap", flex: "0 0 auto", flexShrink: 0 }}>글 공개 여부</b>
          <BTCheckBox title="" checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} />
        </div>

        <BTBox height="16px" />
        <Divider style={{ width: "100%", backgroundColor: '#D9D9D9' }} />
        <TitleMediumBold style={{ marginTop: "20px" }}>채용공고 컨텐츠 입력</TitleMediumBold>
        <BTBox height="16px" />

        <BTEditor
            title={jobTitle}
            contents={contents}
            selectedImage={selectedImage}
            uploadedFiles={uploadedFiles}
            uploadedFileUrls={uploadedFileUrls}
            setTitle={setJobTitle}
            setContents={setContents}
            setSelectedImage={setSelectedImage}
            setUploadedFiles={setUploadedFiles}
            setUploadedFileUrls={setUploadedFileUrls}
            onSave={handleSave}
            onCancel={() => navigate(-1)}
            attachmentAccept="all"
        />
      </VerticalLayout>
    </VerticalLayout>
  );
});